import { BCMSDate } from "@bcm-dev/bcms-vite";
import Classes, { OutgoingFedWire } from "model";
/**
 * @class SearchForm
 * @classdesc Class definition for controlling the properties that are set in a search feature.
 * 
 * @property {STRING} _status - The status to filter by
 * @property {STRING} _fundingAccountID - The fundingAccountID to filter by
 * @property {STRING} _dateRange - The date range to filter by
 * @property {BCMSDate} _fromDate - The fromDate to filter by
 * @property {BCMSDate} _toDate - The toDate to filter by
 * @property {STRING} _referenceNumber - The referenceNumber to filter by
 * @property {ARRAY} _itemsToFilter - An array of items to be filtered
 * @property {OBJECT} _defaultValues - The default values for the search fields
 * 
 * @description - A class to manage a search field.
 */
export default class SearchForm {
    _itemsToFilter;
    _status;
    _fundingAccountID;
    _dateRange;
    _fromDate;
    _toDate;
    _referenceNumber;
    _defaultValues;

    constructor(record) {
        this.defaultValues = { 
            itemsToFilter: OutgoingFedWire.initAll(),
            status: "any status",
            fundingAccountID: "all accounts",
            dateRange: "custom",
            fromDate: BCMSDate.init().offset(-7),
            toDate: undefined,
            referenceNumber: ""
        };

        this.itemsToFilter = record.itemsToFilter ?? this.defaultValues.itemsToFilter;
        this.status = record.status ?? this.defaultValues.status;
        this.fundingAccountID = record.fundingAccountID ?? this.defaultValues.fundingAccountID;
        this.dateRange = record.dateRange ?? this.defaultValues.dateRange;
        this.fromDate = record.fromDate ?? this.defaultValues.fromDate;
        this.toDate = record.toDate ?? this.defaultValues.toDate;
        this.referenceNumber = record.referenceNumber ?? this.defaultValues.referenceNumber;
    }

    set itemsToFilter(value) { this._itemsToFilter = value; }
    set status(value) { this._status = value; }
    set fundingAccountID(value) { this._fundingAccountID = value; }
    set dateRange(value) { this._dateRange = value; }
    set fromDate(value) { this._fromDate = value; }
    set toDate(value) { this._toDate = value; }
    set referenceNumber(value) { this._referenceNumber = value; }
    set defaultValues(value) { this._defaultValues = value; }

    get itemsToFilter() { return this._itemsToFilter; }
    get status() { return this._status; }
    get fundingAccountID() { return this._fundingAccountID; }
    get dateRange() { return this._dateRange; }
    get fromDate() { return this._fromDate; }
    get toDate() { return this._toDate; }
    get referenceNumber() { return this._referenceNumber; }
    get defaultValues() { return this._defaultValues; }

    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof WireSearch
     */
    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    get filters() {
        const res = Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && key !== "itemsToFilter") {
                result[key] = value;
            }

            return result;
        }, {});

        return res;
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof WireSearch
     */
    get record() {
        return {
            itemsToFilter: this.itemsToFilter,
            status: this.status,
            fundingAccountID: this.fundingAccountID,
            dateRange: this.dateRange,
            fromDate: this.fromDate,
            toDate: this.toDate,
            referenceNumber: this.referenceNumber
        };
    }

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    checkValue(item, key) {
        switch(key) {
            case "status":
                return item.status === this.status || this.status === "any status";
            case "fundingAccountID":
                return this.fundingAccountID === "all accounts" || item.fundingAccount.id === this.fundingAccountID;
            case "fromDate":
                return item.wireDate.isEqual(this.fromDate) || item.wireDate.isAfter(this.fromDate);
            case "toDate":
                return item.wireDate.isEqual(this.toDate) || item.wireDate.isBefore(this.toDate);
            case "referenceNumber":
                return this.referenceNumber === "" || item.referenceNumber === this.referenceNumber;
            default:
                return true;
        }
    }

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    filter() {
        return this.itemsToFilter
            .filter(item => {
                return Object.keys(this.filters).reduce((result, key) => {
                    if(!this.checkValue(item, key)) {
                        result = false;
                    }

                    return result;
                }, true);
            }, []);
    }

    reset() {
        return SearchForm.init(this.defaultValues);
    }
    
    static init(record) {
        return new SearchForm(record ?? {});
    }
}